import { useMemo } from 'react';

import { CellValueChangedEvent, ICellRendererParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

import { ResponsiveDataView } from '@/components/common';

export type DonorRow = {
  id: string;
  opuSite: string;
  matTime: string;
  totalOocytes: number;
  splitOocytes: number;
  donorLocation: string;
  donor: string;
  donorBreed: string;
  sire: string;
  sireNote: string;
  semenPullSireNote: string;
  sireBreed: string;
  collectionCode?: string;
  semenType?: string;
  semenComments: string;
  semenOwner?: string;
  separateFert: boolean;
  account: string;
  shipLabel: string;
  exportCode?: string;
  split?: string;
  revision?: string;
  revisionComment?: string;
  hasIssues?: boolean;
  semenUnits?: number | null;
};

export type ValidationGridProps = {
  rowData: DonorRow[];
  onEdit?: (donorId: string) => void;
  onRemove?: (donorId: string) => void;
  onCellValueChanged?: (event: CellValueChangedEvent) => void;
  hasWritePermission?: boolean;
};

const ActionsRenderer = (props: ICellRendererParams) => {
  const { t } = useTranslation();
  const { onEdit, onRemove, hasWritePermission } = props.context;

  if (!hasWritePermission) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-blue-600 transition-colors hover:text-blue-800 hover:underline"
        onClick={() => onEdit?.(props.data.id)}
      >
        {t('fertWork.edit', 'Edit')}
      </button>
      <span className="text-gray-300">|</span>
      <button
        type="button"
        className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-red-600 transition-colors hover:text-red-800 hover:underline"
        onClick={() => onRemove?.(props.data.id)}
      >
        {t('fertWork.remove', 'Remove')}
      </button>
    </div>
  );
};

const SplitRenderer = (props: ICellRendererParams) => {
  const isClientSplit = isClientSpecifiedSplit(props.data.donor);
  if (!isClientSplit) return <span>-</span>;
  return <span className="font-medium text-green-600">{props.value}</span>;
};

const isClientSpecifiedSplit = (donorName: string): boolean => {
  if (!donorName) return false;
  return /\((\d+)\/(\d+)\)/.test(donorName);
};

const TotalOocytesRenderer = (params: ICellRendererParams) => {
  const hasSplit = isClientSpecifiedSplit(params.data.donor);
  return (
    <div className="flex items-center gap-1">
      <span>{params.value}</span>
      {hasSplit && <i className="pi pi-arrows-h text-sm text-green-600" />}
    </div>
  );
};

const DonorRenderer = (params: ICellRendererParams) => {
  const revision = params.data.revision as string | undefined;
  const revisionComment = params.data.revisionComment as string | undefined;
  const hasRevision = !!(revision || revisionComment);
  const tooltip = hasRevision
    ? [revision && `Revision: ${revision}`, revisionComment && revisionComment]
        .filter(Boolean)
        .join('\n')
    : undefined;
  return (
    <div className="flex items-center gap-1.5">
      {hasRevision && (
        <i
          className="pi pi-history cursor-help text-xs text-amber-600"
          title={tooltip}
          aria-label="Has revision"
        />
      )}
      <span>{params.value}</span>
    </div>
  );
};

const missingValueCellStyle = (
  params: any,
): { color: string; fontWeight: string } | undefined =>
  params.data.hasIssues && !params.value
    ? { color: 'red', fontWeight: 'bold' }
    : undefined;

const warningValueCellStyle = (
  params: any,
): { color: string; fontWeight: string } | undefined => {
  if (!params.value) {
    return { color: '#d97706', fontWeight: 'bold' };
  }
  return undefined;
};

// WI 35152: terms that trigger the semen comment highlight
const SEMEN_COMMENT_HIGHLIGHT_TERMS = [
  'pool',
  'mix',
  'both',
  'combine',
  '1/2',
  'half',
  'cut',
];

export const shouldHighlightSemenComment = (value: unknown): boolean => {
  const text = String(value ?? '').toLowerCase();
  return SEMEN_COMMENT_HIGHLIGHT_TERMS.some((term) => text.includes(term));
};

const semenCommentHighlightStyle = (
  params: any,
): { backgroundColor: string } | undefined =>
  shouldHighlightSemenComment(params.value)
    ? { backgroundColor: '#FFFF00' }
    : undefined;

const DonorCard = ({
  donor,
  onEdit,
  onRemove,
  hasWritePermission,
}: {
  donor: DonorRow;
  onEdit?: (id: string) => void;
  onRemove?: (id: string) => void;
  hasWritePermission: boolean;
}) => {
  const { t } = useTranslation();
  const hasSplit = isClientSpecifiedSplit(donor.donor);

  return (
    <div
      className={`rounded-lg border bg-white p-4 ${donor.hasIssues ? 'border-red-300' : 'border-gray-200'}`}
    >
      <div className="mb-3 flex items-start justify-between">
        <div>
          <div className="font-semibold text-gray-900">{donor.donor}</div>
          <div className="text-sm text-gray-600">{donor.donorBreed}</div>
        </div>
        <div className="flex flex-col items-end">
          <div className="flex items-center gap-1">
            <span className="text-lg font-bold">{donor.totalOocytes}</span>
            {hasSplit && (
              <i className="pi pi-arrows-h text-sm text-green-600" />
            )}
          </div>
          <span className="text-xs text-gray-500">
            {t('fertWork.oocytes', 'oocytes')}
          </span>
        </div>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-gray-500">{t('fertWork.sire', 'Sire')}:</span>{' '}
          <span
            className={
              !donor.sire && donor.hasIssues ? 'font-bold text-red-600' : ''
            }
          >
            {donor.sire || t('fertWork.missing', 'Missing')}
          </span>
        </div>
        <div>
          <span className="text-gray-500">
            {t('fertWork.matTime', 'Mat Time')}:
          </span>{' '}
          {donor.matTime || '-'}
        </div>
        <div>
          <span className="text-gray-500">
            {t('fertWork.account', 'Account')}:
          </span>{' '}
          <span
            className={
              !donor.account && donor.hasIssues ? 'font-bold text-red-600' : ''
            }
          >
            {donor.account || t('fertWork.missing', 'Missing')}
          </span>
        </div>
        <div>
          <span className="text-gray-500">
            {t('fertWork.shipLabel', 'Ship Label')}:
          </span>{' '}
          <span className={!donor.shipLabel ? 'font-bold text-amber-600' : ''}>
            {donor.shipLabel || t('fertWork.missing', 'Missing')}
          </span>
        </div>
        <div>
          <span className="text-gray-500">
            {t('fertWork.donorLocation', 'Donor Location')}:
          </span>{' '}
          {donor.donorLocation || '-'}
        </div>
        <div>
          <span className="text-gray-500">
            {t('fertWork.separateFert', 'Separate Fert.')}:
          </span>{' '}
          {donor.separateFert
            ? t('fertWork.yes', 'YES')
            : t('fertWork.no', 'NO')}
        </div>
      </div>

      {donor.semenComments && (
        <div className="mb-3 text-sm text-gray-600">
          <span className="text-gray-500">
            {t('fertWork.semenComments', 'Semen Comments')}:
          </span>{' '}
          {/* WI 35152: highlight pooled-sire / half-unit comments, matching the grid + run sheet */}
          <span
            style={
              shouldHighlightSemenComment(donor.semenComments)
                ? { backgroundColor: '#FFFF00' }
                : undefined
            }
          >
            {donor.semenComments}
          </span>
        </div>
      )}

      {donor.semenPullSireNote && (
        <div className="mb-3 text-sm text-gray-600">
          <span className="text-gray-500">
            {t('fertWork.semenPullSireNote', 'Semen Pull Sire Note')}:
          </span>{' '}
          {donor.semenPullSireNote}
        </div>
      )}

      {hasWritePermission && (
        <div className="mt-2 flex items-center justify-end gap-2 border-t border-gray-100 pt-2">
          <button
            type="button"
            className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-blue-600 transition-colors hover:text-blue-800 hover:underline"
            onClick={() => onEdit?.(donor.id)}
          >
            {t('fertWork.edit', 'Edit')}
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-red-600 transition-colors hover:text-red-800 hover:underline"
            onClick={() => onRemove?.(donor.id)}
          >
            {t('fertWork.remove', 'Remove')}
          </button>
        </div>
      )}
    </div>
  );
};

export const ValidationGrid = ({
  rowData,
  onEdit,
  onRemove,
  onCellValueChanged,
  hasWritePermission = false,
}: ValidationGridProps) => {
  const { t } = useTranslation();

  const columnDefs = useMemo(() => {
    const missingValueFormatter = (params: any) =>
      params.value || t('fertWork.missing', 'Missing');
    const separateFertFormatter = (params: any) =>
      params.value ? t('fertWork.yes', 'YES') : t('fertWork.no', 'NO');

    return [
      {
        field: 'opuSite',
        headerName: t('fertWork.opuSite', 'OPU Site'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'matTime',
        headerName: t('fertWork.matTime', 'Mat Time'),
        sortable: true,
        filter: true,
        flex: 1,
        comparator: (a: string, b: string) => {
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          return a.localeCompare(b);
        },
      },
      {
        field: 'totalOocytes',
        headerName: t('fertWork.totalOocytes', 'Total Oocytes'),
        sortable: true,
        filter: true,
        flex: 1,
        cellRenderer: TotalOocytesRenderer,
      },
      {
        field: 'splitOocytes',
        headerName: t('fertWork.splitOocytes', 'Split Oocytes'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'donorLocation',
        headerName: t('fertWork.donorLocation', 'Donor Location'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'donor',
        headerName: t('fertWork.donor', 'Donor'),
        sortable: true,
        filter: true,
        flex: 1,
        cellRenderer: DonorRenderer,
      },
      {
        field: 'donorBreed',
        headerName: t('fertWork.donorBreed', 'Donor Breed'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'sire',
        headerName: t('fertWork.sire', 'Sire'),
        sortable: true,
        filter: true,
        flex: 1,
        cellStyle: missingValueCellStyle,
        valueFormatter: missingValueFormatter,
      },
      {
        field: 'sireNote',
        headerName: t('fertWork.sireNote', 'Sire Note'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        // 35106: read-only reference sourced from Semen Pull; not editable, not used in grouping.
        field: 'semenPullSireNote',
        headerName: t('fertWork.semenPullSireNote', 'Semen Pull Sire Note'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'sireBreed',
        headerName: t('fertWork.sireBreed', 'Sire Breed'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'collectionCode',
        headerName: t('fertWork.collectionCode', 'Collection Code'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'semenType',
        headerName: t('fertWork.semenType', 'Semen Type'),
        sortable: true,
        filter: 'agSetColumnFilter',
        flex: 1,
      },
      {
        field: 'semenComments',
        headerName: t('fertWork.semenComments', 'Semen Comments'),
        sortable: true,
        filter: true,
        flex: 2,
        cellStyle: semenCommentHighlightStyle,
      },
      {
        field: 'semenOwner',
        headerName: t('fertWork.semenOwner', 'Semen Owner'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'semenUnits',
        headerName: t('fertWork.semenUnits', 'Unit #'),
        sortable: true,
        filter: true,
        flex: 1,
        editable: hasWritePermission,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: { min: 0 },
        valueFormatter: (params: any) => (params.value ?? 1).toString(),
        valueParser: (params: any) => {
          const val = Number(params.newValue);
          return Number.isNaN(val) ? params.oldValue : val;
        },
      },
      {
        field: 'separateFert',
        headerName: t('fertWork.separateFert', 'Separate Fert.'),
        sortable: true,
        filter: true,
        flex: 1,
        valueFormatter: separateFertFormatter,
      },
      {
        field: 'account',
        headerName: t('fertWork.account', 'Account'),
        sortable: true,
        filter: true,
        flex: 1,
        cellStyle: missingValueCellStyle,
        valueFormatter: missingValueFormatter,
      },
      {
        field: 'shipLabel',
        headerName: t('fertWork.shipLabel', 'Ship Label'),
        sortable: true,
        filter: true,
        flex: 1,
        cellStyle: warningValueCellStyle,
        valueFormatter: missingValueFormatter,
      },
      {
        field: 'exportCode',
        headerName: t('fertWork.export', 'Export'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'split',
        headerName: t('fertWork.split', 'Split'),
        sortable: true,
        filter: true,
        flex: 1,
        cellRenderer: SplitRenderer,
      },
      {
        field: 'actions',
        headerName: t('fertWork.actions', 'Actions'),
        cellRenderer: ActionsRenderer,
        sortable: false,
        filter: false,
        flex: 1,
        pinned: 'right',
      },
    ];
  }, [t, hasWritePermission]);

  const context = useMemo(
    () => ({ onEdit, onRemove, hasWritePermission }),
    [onEdit, onRemove, hasWritePermission],
  );

  return (
    <ResponsiveDataView<DonorRow>
      rowData={rowData}
      columnDefs={columnDefs}
      storageKey="fert-work-validation-grid"
      getRowId={(row) => row.id}
      renderCard={(donor) => (
        <DonorCard
          donor={donor}
          onEdit={onEdit}
          onRemove={onRemove}
          hasWritePermission={hasWritePermission}
        />
      )}
      emptyMessage={t('fertWork.noDonorsFound', 'No donors found')}
      onCellValueChanged={onCellValueChanged}
      context={context}
    />
  );
};
